import { Checkbox, Input, RadioGroup, Select } from '../../inputs';
import {
  FormFieldType,
  FormValidation,
  type FormValidationValue,
  type IFormBase,
  type IFormInput,
  type IFormRadioGroup,
  type IFormSelect,
  type IFormTextArea,
  type IFormTime,
} from '../../../typings';
import type { HintContent } from '@/ui-kit';
import { singleFieldInnerPresenterRegistry } from './single-field-inner-presenter.registry';
import type { SingleFieldInnerRenderer } from './single-field-inner-presenter.registry';
import { useLookupEntryAddressOptions } from '../../../hooks/use-lookup-entry-address-options';
import { NO_PRACTICE_LOCATIONS } from '../../../utils/practiceSlots';

function dateBoundaryFromValidation(
  validation: FormValidationValue | undefined,
): Date | undefined {
  const raw = validation?.value;
  if (raw === undefined || raw === null || raw === '') {
    return undefined;
  }

  const d = new Date(String(raw));

  return Number.isNaN(d.getTime()) ? undefined : d;
}

/** The field's "?" content, or nothing when the field has no hint. */
const hintOf = (f: IFormBase): HintContent | undefined =>
  f.hint !== undefined ? { text: f.hint, imageUrl: f.hintImageUrl } : undefined;

const SelectRenderer: SingleFieldInnerRenderer = ({
  field,
  disabled,
  fieldName,
  isRequired,
  maxLengthValidation,
}) => {
  // Before the type check: a hook runs on every render.
  const options = useLookupEntryAddressOptions(field as IFormSelect, fieldName);
  if (field.type !== FormFieldType.Select) return null;
  const f = field as IFormSelect;
  // A practice dropdown offers only the locations on the application, which can be none yet.
  const offersNoPractice =
    Boolean(f.lookupEntryAddresses?.length) && options?.length === 0;
  return (
    <Select
      name={fieldName}
      multiple={f.multiple}
      label={f.label}
      options={options}
      disabled={f.readonly || disabled}
      required={isRequired}
      hint={hintOf(f)}
      canAddCustom={f.canAddEntry}
      maxSearchLength={maxLengthValidation?.value}
      noOptionsText={offersNoPractice ? NO_PRACTICE_LOCATIONS : undefined}
    />
  );
};

const CheckboxRenderer: SingleFieldInnerRenderer = ({
  field,
  disabled,
  fieldName,
}) => (
  <Checkbox
    name={fieldName}
    label={field.label}
    disabled={field.readonly || disabled}
  />
);

const RadioGroupRenderer: SingleFieldInnerRenderer = ({
  field,
  disabled,
  fieldName,
}) => {
  if (field.type !== FormFieldType.RadioGroup) return null;
  const f = field as IFormRadioGroup;
  return (
    <RadioGroup
      label={f.label}
      name={fieldName}
      options={f.options}
      disabled={f.readonly || disabled}
    />
  );
};

const DateRenderer: SingleFieldInnerRenderer = ({
  field,
  disabled,
  fieldName,
  isRequired,
  warning,
}) => {
  const minDateValidation = dateBoundaryFromValidation(
    field.validations?.find(
      v => v.type === FormValidation.MinValue,
    ) as FormValidationValue | undefined,
  );

  const maxDateValidation = dateBoundaryFromValidation(
    field.validations?.find(
      v => v.type === FormValidation.MaxValue,
    ) as FormValidationValue | undefined,
  );

  return (
    <Input.Date
      label={field.label}
      name={fieldName}
      placeholder={field.placeholder}
      required={isRequired}
      disabled={field.readonly || disabled}
      warning={warning}
      {...(maxDateValidation && { maxDate: maxDateValidation })}
      {...(minDateValidation && { minDate: minDateValidation })}
    />
  );
};

const TimeRenderer: SingleFieldInnerRenderer = ({
  field,
  disabled,
  fieldName,
  isRequired,
}) => {
  if (field.type !== FormFieldType.Time) return null;
  const f = field as IFormTime;
  return (
    <Input.Time
      name={fieldName}
      label={f.label}
      required={isRequired}
      disabled={f.readonly || disabled}
    />
  );
};

const TextAreaRenderer: SingleFieldInnerRenderer = ({
  field,
  disabled,
  fieldName,
  isRequired,
  maxLengthValidation,
}) => {
  if (field.type !== FormFieldType.TextArea) return null;
  const f = field as IFormTextArea;
  return (
    <Input.TextArea
      name={fieldName}
      label={f.label}
      placeholder={f.placeholder}
      helperText={f.helperText}
      required={isRequired}
      disabled={f.readonly || disabled}
      hint={hintOf(f)}
      maxLength={maxLengthValidation?.value}
    />
  );
};

const DefaultTextInputRenderer: SingleFieldInnerRenderer = ({
  field,
  disabled,
  fieldName,
  isRequired,
  maxLengthValidation,
}) => {
  if (field.type !== FormFieldType.Input) return null;
  const f = field as IFormInput;
  return (
    <Input.Text
      type={f.inputType}
      name={fieldName}
      label={f.label}
      placeholder={f.placeholder}
      required={isRequired}
      disabled={f.readonly || disabled}
      hint={hintOf(f)}
      showClearButton={f.showClearButton}
      maxLength={maxLengthValidation?.value}
      suggestions={f.suggestedValues}
      suggestionsErrorKey={f.restrictionErrorKey}
      {...(f.mask ? f.mask : {})}
    />
  );
};

singleFieldInnerPresenterRegistry
  .setFallback(DefaultTextInputRenderer)
  .register(FormFieldType.Select, SelectRenderer)
  .register(FormFieldType.Checkbox, CheckboxRenderer)
  .register(FormFieldType.RadioGroup, RadioGroupRenderer)
  .register(FormFieldType.Date, DateRenderer)
  .register(FormFieldType.Time, TimeRenderer)
  .register(FormFieldType.TextArea, TextAreaRenderer);
